import type { Robot, ApiEnvelope } from '@/types/api'
import { api } from './client'


// Lấy danh sách sơ đồ sàn
export async function fetchFloorplans(): Promise<{ id: string; name: string; imageUrl: string }[]> {
  const response = await api.get<ApiEnvelope<{ id: string; name: string; imageUrl: string }[]>>('/v1/floorplan/list')
  return response.data.responseData
}

// Lấy danh sách trạm sạc theo sơ đồ sàn
export async function fetchChargersByFloorplan(floorplanId: string): Promise<{ id: string; name: string }[]> {
  const response = await api.get<ApiEnvelope<{ id: string; name: string }[]>>(`/v1/charger/list/${floorplanId}`)
  return response.data.responseData
}

// Lấy danh sách vị trí của robot (API mới)
export async function fetchRobotPositions(
  robotId: string,
  page = 0,
  size = 10,
  locationName?: string,
  signal?: AbortSignal,
  mapFileUrl?: string
): Promise<any[]> {
  const response = await api.get<ApiEnvelope<any>>(`/v1/robot/location/search/${robotId}`, {
    params: {
      page,
      size,
      searchType: 'ALL',
      ...(locationName ? { locationName } : {}),
    },
    signal,
  })

  const items = response.data.responseData?.items || response.data.responseData?.content || []
  if (!Array.isArray(items) || items.length === 0) return []

  let slamJson: {
    robot?: { x?: number; y?: number }
    obstacles?: Array<{ x?: number; y?: number }>
  } = { obstacles: [], robot: {} }

  try {
    const slamMapResponse = await fetch(mapFileUrl || '', { signal })
    const contentType = slamMapResponse.headers.get('content-type')
    if (slamMapResponse.ok && contentType && contentType.includes('application/json')) {
      slamJson = (await slamMapResponse.json())
    } else {
      console.warn("Định dạng file SLAM không hợp lệ hoặc lỗi tải file, sử dụng fallback.")
    }
  } catch (error) {
    console.error("Lỗi khi đọc file cấu hình SLAM:", error)
  }

  const points: Array<{ x: number; y: number }> = []
  if (
    slamJson.robot &&
    typeof slamJson.robot.x === 'number' &&
    typeof slamJson.robot.y === 'number'
  ) {
    points.push({ x: slamJson.robot.x, y: slamJson.robot.y })
  }

  for (const o of slamJson.obstacles ?? []) {
    if (typeof o.x === 'number' && typeof o.y === 'number') {
      points.push({ x: o.x, y: o.y })
    }
  }

  const mapItemToPosition = (item: any, x: number, y: number) => ({
    id: String(item.id),
    name: item.name,
    x,
    y,
    rawX: x,
    rawY: y,
    yaw:
      typeof item?.yaw === 'number'
        ? item.yaw
        : typeof item?.yawDeg === 'number'
          ? item.yawDeg
          : Number(item?.yaw ?? item?.yawDeg ?? 0),
  })

  if (points.length === 0) {
    return items
      .map((item) => {
        const rawX = item?.longitude ?? item?.x ?? item?.positionX ?? item?.posX ?? item?.coordX
        const rawY = item?.latitude ?? item?.y ?? item?.positionY ?? item?.posY ?? item?.coordY
        const x = typeof rawX === 'number' ? rawX : Number(rawX)
        const y = typeof rawY === 'number' ? rawY : Number(rawY)
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null
        return mapItemToPosition(item, x, y)
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
  }

  const minX = Math.min(...points.map((p) => p.x))
  const maxX = Math.max(...points.map((p) => p.x))
  const minY = Math.min(...points.map((p) => p.y))
  const maxY = Math.max(...points.map((p) => p.y))

  const toPercentX = (x: number) => (maxX === minX ? 0 : ((x - minX) / (maxX - minX)) * 100)
  const toPercentY = (y: number) => (maxY === minY ? 0 : ((y - minY) / (maxY - minY)) * 100)

  return items
    .map((item) => {
      const rawX = item?.longitude ?? item?.x ?? item?.positionX ?? item?.posX ?? item?.coordX
      const rawY = item?.latitude ?? item?.y ?? item?.positionY ?? item?.posY ?? item?.coordY

      const slamX = typeof rawX === 'number' ? rawX : Number(rawX)
      const slamY = typeof rawY === 'number' ? rawY : Number(rawY)
      if (!Number.isFinite(slamX) || !Number.isFinite(slamY)) return null

      return {
        id: String(item.id),
        name: item.name,
        x: toPercentX(slamX),
        y: toPercentY(slamY),
        rawX: slamX,
        rawY: slamY,
        yaw:
          typeof item?.yaw === 'number'
            ? item.yaw
            : typeof item?.yawDeg === 'number'
              ? item.yawDeg
              : Number(item?.yaw ?? item?.yawDeg ?? 0),
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
}



// Lưu danh sách vị trí cho robot
export async function saveRobotPositions(robotId: string, positions: Robot['positions']): Promise<any> {
  return api.post(`/v1/robot/config/positions/${robotId}`, { positions })
}
